import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  DragEvent,
} from "react";

import { Button } from "react-bootstrap";

import { v4 as uuid } from "uuid";

import { ReferentialType } from "@/_constants/referentialType";
import SelectCreatable from "@/common/components/SelectCreatable";
import { IReferentials } from "@/modules/referential/reducers";

interface UploadDocumentFormProps {
  documents: any[];
  setDocuments: Dispatch<SetStateAction<any[]>>;
  referentials: IReferentials;
  onUpload?: (documents: DocumentItem[]) => Promise<void>;
  isUploading?: boolean;
  showUploadButton?: boolean;
}

interface DocumentItem {
  key: string;
  content: File | null;
  type: any;
  url?: string;
  name?: string;
}

const UploadDocumentForm = ({
  documents,
  setDocuments,
  referentials,
  onUpload,
  isUploading = false,
  showUploadButton = false,
}: UploadDocumentFormProps) => {
  const [documentComp, setDocumentComp] = useState<DocumentItem[]>(
    documents.map((document) => ({
      key: document?.updateInfo?.id || document?.id || uuid(),
      content: null,
      type: document?.updateInfo?.type || document?.type || null,
      url: document.url,
      name:
        document.name || document.url?.split("/").pop() || "Document existant",
    }))
  );

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const newDocuments = files.map((file) => ({
      key: uuid(),
      content: file,
      type: null,
      name: file.name,
    }));

    setDocumentComp((prev) => [...prev, ...newDocuments]);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const newDocuments = files.map((file) => ({
        key: uuid(),
        content: file,
        type: null,
        name: file.name,
      }));

      setDocumentComp((prev) => [...prev, ...newDocuments]);
      e.target.value = "";
    },
    []
  );

  const updateDocumentType = (key: string, type: any) => {
    setDocumentComp((prev) =>
      prev.map((doc) => (doc.key === key ? { ...doc, type } : doc))
    );
  };

  const deleteDocument = (keyToRemove: string) => {
    setDocumentComp((prev) => prev.filter((doc) => doc.key !== keyToRemove));
  };

  const handleUpload = async () => {
    if (!onUpload) return;
    
    try {
      const documentsToUpload = documentComp.filter(doc => doc.content && doc.type);
      await onUpload(documentsToUpload);
      setDocumentComp((prev) => prev.filter(doc => !doc.content || !doc.type));
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const canUpload = () => {
    const documentsWithContent = documentComp.filter(doc => doc.content);
    return documentsWithContent.length > 0 && 
           documentsWithContent.every(doc => doc.type && doc.type.value && doc.type.label);
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "pi-file-pdf";
      case "doc":
      case "docx":
        return "pi-file-word";
      case "xls":
      case "xlsx":
        return "pi-file-excel";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "pi-image";
      default:
        return "pi-file";
    }
  };

  useEffect(() => {
    const cleanedDocuments = documentComp.map((doc) => {
      const cleanedDoc = { ...doc };
      if (
        !cleanedDoc.type ||
        !cleanedDoc.type.value ||
        !cleanedDoc.type.label ||
        cleanedDoc.type.value === "" ||
        cleanedDoc.type.label === ""
      ) {
        delete cleanedDoc.type;
      }
      return cleanedDoc;
    });

    setDocuments(cleanedDocuments);
  }, [documentComp, setDocuments]);

  return (
    <div className="space-y-6 position-relative">
      {/* Loading overlay */}
      {isUploading && (
        <div
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1000,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div className="d-flex flex-column align-items-center gap-2">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <span className="text-primary">Téléchargement en cours...</span>
          </div>
        </div>
      )}

      {/* Multi-file Dropzone */}
      <div
        className={`
          relative border-2 border-dashed rounded transition-all duration-200 cursor-pointer p-3 my-2 border-[#DEE2E6] bg-[#f2f4f7]
          ${
            isDragOver
              ? "!border-[#3b82f6] !bg-[#eff6ff] scale-105"
              : ""
          }
          ${isUploading ? "opacity-50 cursor-not-allowed" : ""}
        `}
        onDragOver={!isUploading ? handleDragOver : undefined}
        onDragLeave={!isUploading ? handleDragLeave : undefined}
        onDrop={!isUploading ? handleDrop : undefined}
        onClick={!isUploading ? () => document.getElementById("file-input")?.click() : undefined}
      >
        <div className="flex flex-col items-center justify-center space-y-3 text-[#6C757D]">
          <i className="pi pi-upload fs-3 mb-2"></i>
          <div className="text-lg font-medium]">
            Glissez-déposez vos fichiers ici
          </div>
          <div className="text-sm">
            ou cliquez pour sélectionner des fichiers
          </div>
          <div className="text fs-10 mt-2">
            PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG
          </div>
          <input
            id="file-input"
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInput}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            disabled={isUploading}
          />
        </div>
      </div>

      {/* File List */}
      {documentComp.length > 0 && (
        <div className="space-y-4">
          {documentComp.map((doc) => (
            <div
              key={doc.key}
              className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 rounded m-2"
            >
              <div className="flex items-center gap-2 flex-1">
                <div className="flex-shrink-0 ">
                  <i className={"fs-4 text-[#6C757D] pi" +" " + getFileIcon(doc.name || "")}/>
                </div>
                <div className="flex-1  min-w-0">
                  <div className="text-[#393e43] truncate max-w-[400px]">
                    {doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {doc.name}
                      </a>
                    ) : (
                      doc.name
                    )}
                  </div>
                  {doc.content && (
                    <div className="text-[12px] text-[#6C757D]">
                      {(doc.content.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="min-w-48">
                  <SelectCreatable
                    options={referentials[ReferentialType.FILE_TYPE].data}
                    getValue={(value) => {
                      if (value && value.value && value.label) {
                        updateDocumentType(doc.key, {
                          value: value.value,
                          label: value.label,
                        });
                      } else {
                        updateDocumentType(doc.key, null);
                      }
                    }}
                    value={doc.type}
                    isMulti={false}
                    creatable={true}
                    label="Sélectionner un type..."
                    disabled={!!doc.url || isUploading}
                  />
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => deleteDocument(doc.key)}
                  className="flex items-center justify-center rounded-lg"
                  title="Supprimer le fichier"
                  disabled={isUploading}
                >
                  <i className="bi bi-trash text-sm"></i>
                </Button>
              </div>
            </div>
          ))}
           <span className="text-[14px] font-medium text-[#6C757D] ">
            Fichiers sélectionnés ({documentComp.length})
          </span>
        </div>
      )}

      {/* Upload button */}
      {showUploadButton && documentComp.some(doc => doc.content) && (
        <div className="d-flex justify-content-end">
          <Button
            variant="success"
            onClick={handleUpload}
            disabled={!canUpload() || isUploading}
            className="d-flex align-items-center gap-2"
          >
            <i className="pi pi-upload"></i>
            {isUploading ? "Téléchargement..." : "Ajouter"}
          </Button>
        </div>
      )}
    </div>
  );
};
export default UploadDocumentForm;
